import React from "react";
import { Printer } from "lucide-react";
import { Button } from "../ui/button";
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from "../ui/dialog";
import { formatDate, formatCurrency } from "../../lib/contractUtils";

export function AddendumPreviewDialog({ previewAddendum, onClose, contract }) {
  const handlePrint = () => {
    const printContent = document.getElementById("addendum-print-area");
    if (!printContent) return;
    const isApproved = previewAddendum?.status === "approved";
    const watermarkText = isApproved ? "APPROVED" : "DRAFT";
    const watermarkColor = isApproved ? "rgba(16,185,129,0.08)" : "rgba(100,116,139,0.08)";
    const html = `<html><head><title>${(previewAddendum?.title || "Addendum").replace(/[<>"'&]/g, "")}</title><style>
      body { font-family: 'Georgia', serif; color: #1a1a1a; max-width: 700px; margin: 40px auto; padding: 0 20px; line-height: 1.6; position: relative; }
      .watermark { position: fixed; top: 50%; left: 50%; transform: translate(-50%, -50%) rotate(-45deg); font-size: 100px; font-weight: bold; color: ${watermarkColor}; pointer-events: none; z-index: 0; letter-spacing: 10px; }
      .content-wrap { position: relative; z-index: 1; }
      h1 { font-size: 22px; text-align: center; border-bottom: 2px solid #333; padding-bottom: 12px; margin-bottom: 24px; text-transform: uppercase; letter-spacing: 1px; }
      h2 { font-size: 16px; margin-top: 28px; margin-bottom: 8px; text-transform: uppercase; letter-spacing: 0.5px; border-bottom: 1px solid #ccc; padding-bottom: 4px; }
      table { width: 100%; border-collapse: collapse; margin-bottom: 24px; }
      td { padding: 6px 12px; border: 1px solid #ddd; font-size: 13px; }
      td:first-child { font-weight: bold; width: 160px; background: #f8f8f8; }
      .sig-block { display: inline-block; width: 45%; vertical-align: top; margin-top: 40px; }
      .sig-line { border-bottom: 1px solid #333; margin-bottom: 4px; height: 40px; }
      @media print { body { margin: 20px; } .watermark { position: fixed; } }
    </style></head><body><div class="watermark">${watermarkText}</div><div class="content-wrap">${printContent.innerHTML}</div></body></html>`;
    const blob = new Blob([html], { type: "text/html" });
    const url = URL.createObjectURL(blob);
    const printWindow = window.open(url, "_blank", "width=800,height=1000");
    printWindow.onload = () => { URL.revokeObjectURL(url); printWindow.focus(); printWindow.print(); };
  };

  if (!previewAddendum) return null;

  const isApproved = previewAddendum.status === "approved";
  const watermarkText = isApproved ? "APPROVED" : "DRAFT";
  const watermarkColor = isApproved ? "rgba(16,185,129,0.07)" : "rgba(100,116,139,0.07)";

  return (
    <Dialog open={!!previewAddendum} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto p-0">
        <div className="p-6 print:p-0">
          <div className="flex items-center justify-between mb-4 print:hidden">
            <DialogTitle>Addendum Draft Preview</DialogTitle>
            <Button variant="outline" size="sm" className="gap-2 rounded-sm" data-testid="print-addendum-btn" onClick={handlePrint}>
              <Printer size={14} />Save as PDF
            </Button>
          </div>

          <div id="addendum-print-area" style={{ position: "relative" }}>
            {/* Watermark */}
            <div style={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%) rotate(-45deg)", fontSize: "90px", fontWeight: "bold", color: watermarkColor, pointerEvents: "none", zIndex: 0, letterSpacing: "10px", whiteSpace: "nowrap" }} data-testid="addendum-watermark">
              {watermarkText}
            </div>

            <div style={{ position: "relative", zIndex: 1 }}>
              <h1 style={{ fontSize: "20px", textAlign: "center", borderBottom: "2px solid #333", paddingBottom: "12px", marginBottom: "24px", textTransform: "uppercase", letterSpacing: "1px", fontFamily: "Georgia, serif" }}>
                AGREEMENT ADDENDUM
              </h1>

              <table className="w-full border-collapse mb-6 text-sm" style={{ borderCollapse: "collapse" }}>
                <tbody>
                  <tr><td className="border p-2 font-semibold bg-slate-50 w-40">Title</td><td className="border p-2">{previewAddendum.title}</td></tr>
                  <tr><td className="border p-2 font-semibold bg-slate-50">Type</td><td className="border p-2 capitalize">{previewAddendum.addendum_type}</td></tr>
                  <tr>
                    <td className="border p-2 font-semibold bg-slate-50">Status</td>
                    <td className="border p-2">
                      <span className={`inline-block px-2 py-0.5 rounded text-xs font-semibold uppercase ${isApproved ? "bg-emerald-100 text-emerald-800" : "bg-slate-100 text-slate-600"}`}>
                        {(previewAddendum.status || "").replace(/_/g, " ")}
                      </span>
                    </td>
                  </tr>
                  <tr><td className="border p-2 font-semibold bg-slate-50">Contract</td><td className="border p-2">{contract?.title} {contract?.custom_fields?.contract_unique_id ? `(${contract.custom_fields.contract_unique_id})` : ""}</td></tr>
                  <tr><td className="border p-2 font-semibold bg-slate-50">Supplier</td><td className="border p-2">{contract?.contracting_party?.legal_name || "-"}</td></tr>
                  {contract?.contracting_party?.registered_address && <tr><td className="border p-2 font-semibold bg-slate-50">Supplier Address</td><td className="border p-2">{contract.contracting_party.registered_address}</td></tr>}
                  {previewAddendum.effective_date && <tr><td className="border p-2 font-semibold bg-slate-50">Effective Date</td><td className="border p-2">{formatDate(previewAddendum.effective_date)}</td></tr>}
                  {previewAddendum.new_expiration_date && <tr><td className="border p-2 font-semibold bg-slate-50">New Expiry Date</td><td className="border p-2">{formatDate(previewAddendum.new_expiration_date)}</td></tr>}
                  {previewAddendum.new_value && <tr><td className="border p-2 font-semibold bg-slate-50">New Contract Value</td><td className="border p-2">{formatCurrency(previewAddendum.new_value)}</td></tr>}
                  <tr><td className="border p-2 font-semibold bg-slate-50">Created By</td><td className="border p-2">{previewAddendum.created_by_name}</td></tr>
                  <tr><td className="border p-2 font-semibold bg-slate-50">Date Created</td><td className="border p-2">{formatDate(previewAddendum.created_at)}</td></tr>
                </tbody>
              </table>

              {previewAddendum.description && (
                <div className="mb-5">
                  <h2 style={{ fontSize: "15px", fontFamily: "Georgia, serif", textTransform: "uppercase", borderBottom: "1px solid #ccc", paddingBottom: "4px", marginBottom: "8px" }}>Description</h2>
                  <p className="text-sm whitespace-pre-wrap" style={{ lineHeight: "1.6" }}>{previewAddendum.description}</p>
                </div>
              )}

              {previewAddendum.changes_summary && (
                <div className="mb-5">
                  <h2 style={{ fontSize: "15px", fontFamily: "Georgia, serif", textTransform: "uppercase", borderBottom: "1px solid #ccc", paddingBottom: "4px", marginBottom: "8px" }}>Summary of Changes</h2>
                  <p className="text-sm whitespace-pre-wrap" style={{ lineHeight: "1.6" }}>{previewAddendum.changes_summary}</p>
                </div>
              )}

              {previewAddendum.sections && previewAddendum.sections.length > 0 && (
                previewAddendum.sections.map((section, idx) => (
                  <div key={idx} className="mb-5">
                    <h2 style={{ fontSize: "15px", fontFamily: "Georgia, serif", textTransform: "uppercase", borderBottom: "1px solid #ccc", paddingBottom: "4px", marginBottom: "8px" }}>
                      {section.name}
                      {!section.is_editable && <span style={{ fontSize: "10px", color: "#888", fontStyle: "italic", marginLeft: "8px" }}>(Standard Clause)</span>}
                    </h2>
                    <p className="text-sm whitespace-pre-wrap" style={{ lineHeight: "1.6" }}>{section.content}</p>
                  </div>
                ))
              )}

              {!previewAddendum.sections?.length && previewAddendum.content && (
                <div className="mb-5">
                  <h2 style={{ fontSize: "15px", fontFamily: "Georgia, serif", textTransform: "uppercase", borderBottom: "1px solid #ccc", paddingBottom: "4px", marginBottom: "8px" }}>Content</h2>
                  <p className="text-sm whitespace-pre-wrap" style={{ lineHeight: "1.6" }}>{previewAddendum.content}</p>
                </div>
              )}

              {/* Signatories */}
              <div className="mt-10 pt-6" style={{ borderTop: "2px solid #333" }}>
                <h2 style={{ fontSize: "15px", fontFamily: "Georgia, serif", textTransform: "uppercase", borderBottom: "1px solid #ccc", paddingBottom: "4px", marginBottom: "16px" }}>Signatories</h2>
                <p className="text-sm mb-6" style={{ lineHeight: "1.6", fontFamily: "Georgia, serif" }}>
                  IN WITNESS whereof the Parties or their duly authorised representatives have entered into this Addendum on the date stated at the beginning.
                </p>
                <div style={{ display: "flex", justifyContent: "space-between", gap: "40px" }}>
                  <div style={{ flex: 1 }}>
                    <p className="text-xs font-semibold mb-1 uppercase tracking-wider text-slate-500">The Company</p>
                    <div style={{ borderBottom: "1px solid #333", height: "48px", marginBottom: "4px" }}></div>
                    <p className="text-xs text-slate-500">Signature</p>
                    <div className="mt-4">
                      <p className="text-sm">Name: ________________________</p>
                      <p className="text-sm mt-2">Title: ________________________</p>
                      <p className="text-sm mt-2">Date: ________________________</p>
                    </div>
                  </div>
                  <div style={{ flex: 1 }}>
                    <p className="text-xs font-semibold mb-1 uppercase tracking-wider text-slate-500">The Service Provider / Supplier</p>
                    <div style={{ borderBottom: "1px solid #333", height: "48px", marginBottom: "4px" }}></div>
                    <p className="text-xs text-slate-500">Signature</p>
                    <div className="mt-4">
                      <p className="text-sm">Name: ________________________</p>
                      <p className="text-sm mt-2">Title: ________________________</p>
                      <p className="text-sm mt-2">Date: ________________________</p>
                    </div>
                  </div>
                </div>
              </div>

              {isApproved && (
                <div className="mt-8 p-4 border-2 border-emerald-300 rounded-sm text-center" style={{ background: "rgba(16,185,129,0.05)" }}>
                  <p className="text-lg font-bold text-emerald-700 uppercase tracking-widest">Approved</p>
                  <p className="text-sm text-emerald-600">{formatDate(previewAddendum.created_at)}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
